import {useCallback, useMemo} from 'react'
import {useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'

import {getAvailableMaterialVariations} from './mix.utils'
import {MixVariationType} from './types'

export const useMix = () => {
  const {
    control,
    setValue,
    trigger,
    formState: {defaultValues}
  } = useOrderProcessFormContext()
  const {contracts, materials, restrictMaterialsToSelectedContract} =
    useFieldConfigurationContext('mix')
  const [selectedMaterial, selectedContract, selectedMaterialVariations] = useWatch({
    control,
    name: ['material', 'contract', 'materialVariations']
  })

  const {contract, material: resolvedMaterial} = useMemo(() => {
    const contract = contracts.find((c) => c.contractNumber === selectedContract?.contractNumber)
    // When a contract is selected, find the material within that contract's materials first
    // to get the correct contractItemPositionNumber.
    const material = contract
      ? (contract.materials.find((m) => m.materialId === selectedMaterial?.materialId) ??
        materials.find((m) => m.materialId === selectedMaterial?.materialId))
      : materials.find((m) => m.materialId === selectedMaterial?.materialId)
    return {contract, material}
  }, [contracts, materials, selectedContract?.contractNumber, selectedMaterial?.materialId])

  const selectedMaterialDisplay = resolvedMaterial ?? selectedMaterial

  const {defaultContract, defaultMaterial} = useMemo(() => {
    const defaultContract = contracts.find(
      (c) => c.contractNumber === defaultValues?.contract?.contractNumber
    )
    const defaultMaterial =
      materials.find((m) => m.materialId === defaultValues?.material?.materialId) ??
      defaultValues?.material
    return {defaultContract, defaultMaterial}
  }, [
    contracts,
    materials,
    defaultValues?.contract?.contractNumber,
    defaultValues?.material?.materialId
  ])

  const availableDateRange = contract?.dateRange

  /**
   * Materials of the selected contract when the process restricts materials to it
   * (quote selection, order change); otherwise materials of all contracts.
   */
  const filteredMaterials = useMemo(() => {
    if (restrictMaterialsToSelectedContract && selectedContract?.contractNumber) {
      const selectedContractData = contracts.find(
        (c) => c.contractNumber === selectedContract.contractNumber
      )
      if (selectedContractData) {
        return materials.filter(
          (material) => material.contractNumber === selectedContractData.contractNumber
        )
      }
    }
    return materials
  }, [contracts, materials, restrictMaterialsToSelectedContract, selectedContract?.contractNumber])

  // Materials
  const setMix = (
    materialId: string,
    contractItemPositionNumber?: string,
    contractNumber?: string
  ) => {
    const targetContractNumber = contractNumber ?? selectedContract?.contractNumber
    let result:
      | {contract: (typeof contracts)[0]; material: (typeof contracts)[0]['materials'][0]}
      | undefined

    if (targetContractNumber) {
      const preSelectedContract = contracts.find((c) => c.contractNumber === targetContractNumber)
      if (preSelectedContract) {
        // If contractItemPositionNumber is provided, use it for precise matching
        const materialInContract = contractItemPositionNumber
          ? preSelectedContract.materials.find(
              (m) =>
                m.materialId === materialId &&
                m.contractItemPositionNumber === contractItemPositionNumber
            )
          : preSelectedContract.materials.find((m) => m.materialId === materialId)

        if (materialInContract) {
          result = {contract: preSelectedContract, material: materialInContract}
        }
      }
    }

    // Fallback: search across all contracts if no pre-selected contract or material not found
    if (!result) {
      result = contracts
        .flatMap((c) => c.materials.map((m) => ({contract: c, material: m})))
        .find((x) => {
          if (contractItemPositionNumber) {
            return (
              x.material.materialId === materialId &&
              x.material.contractItemPositionNumber === contractItemPositionNumber
            )
          }
          return x.material.materialId === materialId
        })
    }

    if (result) {
      const {contract: newContract, material: newMaterial} = result
      setValue('contract', {
        contractNumber: newContract.contractNumber,
        contractItemPositionNumber: newMaterial.contractItemPositionNumber
      })
      setValue('material', {
        materialId: newMaterial.materialId,
        materialNumber: newMaterial.materialNumber,
        materialDescription: newMaterial.materialDescription,
        customerMaterialDescription: newMaterial.customerMaterialDescription
      })
      setValue('materialVariations', undefined)
      void trigger(['contract', 'material', 'materialVariations'])
    }
  }

  // Material Variations
  const isMaterialVariationSelectionAvailable = useMemo(() => {
    return !!filteredMaterials.find(
      (material) => getAvailableMaterialVariations(material).isEnabled
    )
  }, [filteredMaterials])

  const availableMaterialVariations = useMemo(
    () => getAvailableMaterialVariations(resolvedMaterial),
    [resolvedMaterial]
  )

  const enableMaterialVariations = async () => {
    const newValue = selectedMaterialVariations ?? {setControl: null, slump: null}
    setValue('materialVariations', newValue)
    await trigger()
  }

  const resetMaterialVariations = async () => {
    if (!availableMaterialVariations.isEnabled) {
      console.warn('Material variation selection is not enabled for user or contract', {
        contract
      })
      setValue('materialVariations', undefined)
      return
    }

    setValue('materialVariations', {setControl: null, slump: null})
    await trigger()
  }

  const setMaterialVariation = useCallback(
    async (value: string, type: MixVariationType) => {
      if (!availableMaterialVariations.isEnabled) {
        return
      }

      // Auto-initialize material variations if not already set
      const currentVariations = selectedMaterialVariations ?? {setControl: null, slump: null}

      const newMaterialVariation = availableMaterialVariations[`${type}s`]?.find(
        (s) => s.value === value
      )
      setValue('materialVariations', {
        ...currentVariations,
        [type]: newMaterialVariation ?? null
      })
      await trigger()
    },
    [availableMaterialVariations, selectedMaterialVariations, setValue, trigger]
  )

  return {
    selectedMaterial: resolvedMaterial,
    selectedMaterialDisplay,
    selectedContract: contract,
    selectedContractItemPositionNumber: selectedContract?.contractItemPositionNumber,
    selectedMaterialVariations: selectedMaterialVariations,
    mixCharacteristics: selectedMaterialVariations, // Alias for compatibility - same as selectedMaterialVariations
    selectedMaterialHasVariations: resolvedMaterial?.materialVariations,
    defaultMaterial,
    defaultContract,
    defaultMaterialVariations: defaultValues?.materialVariations,
    setMix,
    isMaterialVariationSelectionAvailable,
    enableMaterialVariations,
    resetMaterialVariations,
    setMaterialVariation,
    availableMixes: filteredMaterials,
    availableMaterialVariations,
    availableDateRange
  }
}
